import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { TableSurface, headerCellSx, bodyRowSx, TableEmptyState, TypePill } from '../../common/pageKit';
import { formatINR, formatNumber } from '../../../dashboard/primitives';
import { RepPerformance, SalesTarget, TargetInput, TargetPeriodType, TargetScope } from '../../../types/salesAnalytics';
import { formatDateIN } from '../../../pages/sales/salesReportsHelpers';

interface TargetsSectionProps {
  targets: SalesTarget[] | null;
  reps: RepPerformance[] | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onCreate: (input: TargetInput) => Promise<void>;
  onUpdate: (id: number, input: TargetInput) => Promise<void>;
}

const SCOPE_COLOR: Record<TargetScope, string> = { COMPANY: '#0284c7', TEAM: '#7c3aed', USER: '#16a34a' };

const emptyForm: TargetInput = {
  scope: 'COMPANY',
  userId: null,
  teamName: '',
  periodType: 'QUARTER',
  periodStart: '',
  periodEnd: '',
  targetAmount: 0,
  targetDeals: null,
  notes: '',
};

const TargetsSection: React.FC<TargetsSectionProps> = ({ targets, reps, loading, saving, error, onCreate, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TargetInput>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setEditingId(null);
      setFormError(null);
    }
  }, [open]);

  const repOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    (reps ?? []).forEach((r) => map.set(r.userId, r.fullName ?? `User #${r.userId}`));
    return Array.from(map.entries());
  }, [reps]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t: SalesTarget) => {
    setEditingId(t.id);
    setForm({
      scope: t.scope,
      userId: t.userId,
      teamName: t.teamName ?? '',
      periodType: t.periodType,
      periodStart: t.periodStart.slice(0, 10),
      periodEnd: t.periodEnd.slice(0, 10),
      targetAmount: t.targetAmount,
      targetDeals: t.targetDeals,
      notes: t.notes ?? '',
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (form.scope === 'USER' && !form.userId) {
      setFormError('Choose a rep for a USER-scoped target.');
      return;
    }
    if (form.scope === 'TEAM' && !form.teamName?.trim()) {
      setFormError('Team name is required for a TEAM-scoped target.');
      return;
    }
    if (!form.periodStart || !form.periodEnd) {
      setFormError('Period start and end are required.');
      return;
    }
    if (form.periodEnd < form.periodStart) {
      setFormError('Period end cannot be before period start.');
      return;
    }
    if (!Number.isFinite(form.targetAmount) || form.targetAmount < 0) {
      setFormError('Target amount must be a non-negative number.');
      return;
    }

    const payload: TargetInput = {
      ...form,
      userId: form.scope === 'USER' ? form.userId : null,
      teamName: form.scope === 'TEAM' ? form.teamName : null,
      targetDeals: form.targetDeals === null || form.targetDeals === undefined ? null : Number(form.targetDeals),
    };

    try {
      if (editingId !== null) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      setOpen(false);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save target');
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Sales Targets</Typography>
          <Typography variant="caption" color="text.secondary">
            Quota attainment above is only meaningful once a target exists for the period.
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreate}>
          New Target
        </Button>
      </Box>

      <TableSurface>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Scope</TableCell>
                <TableCell sx={headerCellSx}>Owner</TableCell>
                <TableCell sx={headerCellSx}>Period</TableCell>
                <TableCell sx={headerCellSx} align="right">Target Amount</TableCell>
                <TableCell sx={headerCellSx} align="right">Target Deals</TableCell>
                <TableCell sx={headerCellSx}>Notes</TableCell>
                <TableCell sx={headerCellSx} align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? null : !targets || targets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState title="No targets defined" hint="Create a target so quota attainment has something to compare against." />
                  </TableCell>
                </TableRow>
              ) : (
                targets.map((t) => (
                  <TableRow key={t.id} sx={bodyRowSx}>
                    <TableCell>
                      <TypePill label={t.scope} tint={SCOPE_COLOR[t.scope]} />
                    </TableCell>
                    <TableCell>{t.scope === 'COMPANY' ? 'Company-wide' : t.scope === 'TEAM' ? t.teamName : t.userName ?? `User #${t.userId}`}</TableCell>
                    <TableCell>
                      {t.periodType} · {formatDateIN(t.periodStart)} – {formatDateIN(t.periodEnd)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatINR(t.targetAmount)}</TableCell>
                    <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {t.targetDeals === null ? '—' : formatNumber(t.targetDeals)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {t.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(t)} aria-label={`Edit target for ${t.userName ?? t.teamName ?? 'company'}`}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </TableSurface>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId !== null ? 'Edit Sales Target' : 'New Sales Target'}</DialogTitle>
        <DialogContent>
          {(formError || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError || error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Scope"
                value={form.scope}
                onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value as TargetScope }))}
              >
                <MenuItem value="COMPANY">Company</MenuItem>
                <MenuItem value="TEAM">Team</MenuItem>
                <MenuItem value="USER">Individual rep</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Period Type"
                value={form.periodType}
                onChange={(e) => setForm((f) => ({ ...f, periodType: e.target.value as TargetPeriodType }))}
              >
                <MenuItem value="MONTH">Month</MenuItem>
                <MenuItem value="QUARTER">Quarter</MenuItem>
                <MenuItem value="YEAR">Year</MenuItem>
              </TextField>
            </Grid>

            {form.scope === 'USER' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Rep"
                  value={form.userId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value ? Number(e.target.value) : null }))}
                  helperText={repOptions.length === 0 ? 'No reps with pipeline activity found yet.' : ' '}
                >
                  {repOptions.map(([id, name]) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {form.scope === 'TEAM' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Team Name"
                  value={form.teamName ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, teamName: e.target.value }))}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Period Start"
                value={form.periodStart}
                onChange={(e) => setForm((f) => ({ ...f, periodStart: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Period End"
                value={form.periodEnd}
                onChange={(e) => setForm((f) => ({ ...f, periodEnd: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Target Amount (₹)"
                value={form.targetAmount}
                onChange={(e) => setForm((f) => ({ ...f, targetAmount: Number(e.target.value) }))}
                slotProps={{ htmlInput: { min: 0, step: 1000 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Target Deals (optional)"
                value={form.targetDeals ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, targetDeals: e.target.value === '' ? null : Number(e.target.value) }))}
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Notes (optional)"
                value={form.notes ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {editingId !== null ? 'Save Changes' : 'Create Target'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TargetsSection;
